import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Input, Table, Tag, Tooltip, Spin, message } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

import { BiExport } from 'react-icons/bi';
import { FaPen, FaPlus, FaSearch, FaEye } from 'react-icons/fa';
import { FaBoxArchive, FaBoxOpen } from "react-icons/fa6";
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

import '../../../css/UserAdmin/Global.css';
import '../../../css/UserAdmin/SideNav.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';
import { DateRangeFilter } from '../../../utils/tableUtils'; 

dayjs.extend(utc);
dayjs.extend(isBetween);

const API_URL = process.env.REACT_APP_API_URL || '';

const getFacultyDisplayInfo = (facultyRef) => {
    if (!facultyRef) return { fullName: 'N/A', email: 'N/A' };

    const firstName = facultyRef.firstName || '';
    const middleName = facultyRef.middleName && facultyRef.middleName.trim() !== '' ? ` ${facultyRef.middleName.trim()}` : '';
    const lastName = facultyRef.lastName || '';
    let constructedFullName = `${firstName}${middleName} ${lastName}`.trim();

    if (!constructedFullName && facultyRef.userID) {
        constructedFullName = `User (${facultyRef.userID})`;
    } else if (!constructedFullName && facultyRef.email && !firstName && !lastName) {
        constructedFullName = facultyRef.email;
    }

    return {
        fullName: constructedFullName || 'Name Not Available',
        email: facultyRef.email || 'N/A'
    };
};


const FacultyProfileTable = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tableFilters, setTableFilters] = useState({});
    const [sorter, setSorter] = useState({});
    const [showArchived, setShowArchived] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = useCallback(async (fetchParameters) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: fetchParameters.page,
                limit: fetchParameters.pageSize,
                isArchived: String(fetchParameters.showArchived), // Ensure boolean is stringified for URL
            });

            if (fetchParameters.search) queryParams.append('search', fetchParameters.search);

            if (fetchParameters.filters) {
                Object.keys(fetchParameters.filters).forEach(key => {
                    const filterValue = fetchParameters.filters[key];
                    if (filterValue && filterValue.length > 0) { // Ensure filter has values
                        if (Array.isArray(filterValue)) {
                             // Handle date range specifically if 'createdAt' or 'updatedAt'
                            if ((key === 'createdAt' || key === 'updatedAt') && filterValue.length === 2) {
                                queryParams.append(`${key}From`, dayjs(filterValue[0]).startOf('day').toISOString());
                                queryParams.append(`${key}To`, dayjs(filterValue[1]).endOf('day').toISOString());
                            } else {
                                filterValue.forEach(val => queryParams.append(key, val));
                            }
                        } else {
                             queryParams.append(key, filterValue);
                        }
                    }
                });
            }

            if (fetchParameters.sorter?.field) {
                let sortByField = fetchParameters.sorter.field;
                // If your backend expects 'facultyRef.lastName' for sorting by faculty name:
                if (fetchParameters.sorter.field === 'facultyFullNameForDisplay') {
                     sortByField = 'facultyRef.lastName'; // Adjust if your backend sorts differently for populated fields
                } else if (fetchParameters.sorter.field === 'facultyName') { // Keep original if it was 'facultyName'
                    sortByField = 'facultyRef.lastName';
                }
                queryParams.append('sortBy', sortByField);
                queryParams.append('sortOrder', fetchParameters.sorter.order === 'ascend' ? 'asc' : 'desc');
            }

            const response = await fetch(`${API_URL}/api/admin/faculty-profiles?${queryParams.toString()}`);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Server error processing request.' }));
                throw new Error(errorData.message || 'Failed to fetch faculty profiles');
            }
            const result = await response.json();

            const processItem = item => {
                const displayInfo = getFacultyDisplayInfo(item.facultyRef);
                return { 
                    ...item, 
                    key: item._id, 
                    facultyFullNameForDisplay: displayInfo.fullName, 
                    facultyEmailForDisplay: displayInfo.email 
                };
            };

            if (result.data && result.pagination) {
                setDataSource(result.data.map(processItem));
                setPagination({
                    current: parseInt(result.pagination.currentPage, 10),
                    pageSize: parseInt(result.pagination.pageSize, 10),
                    total: parseInt(result.pagination.totalItems, 10),
                });
            } else {
                console.warn("API response structure mismatch. Expected { data: [...], pagination: {...} }", result);
                setDataSource([]);
                setPagination(prev => ({ ...prev, total: 0, current: 1 }));
            }
        } catch (error) {
            console.error("Error in fetchData:", error);
            message.error(error.message || 'Data loading failed.');
            setDataSource([]);
            setPagination(prev => ({ ...prev, total: 0, current: 1 }));
        } finally {
            setLoading(false);
        }
    }, []); // API_URL removed from deps as it's constant, add back if it can change during component lifecycle

    useEffect(() => {
        fetchData({
            page: pagination.current,
            pageSize: pagination.pageSize,
            showArchived: showArchived,
            search: searchTerm,
            filters: tableFilters,
            sorter: sorter
        });
    }, [pagination.current, pagination.pageSize, showArchived, searchTerm, tableFilters, sorter, fetchData]);

    const handleTableChange = (newTablePagination, newFilters, newSorter, extra) => {
        if (extra.action === 'paginate' || extra.action === 'sort' || extra.action === 'filter') {
            setTableFilters(newFilters); // This stores AntD's filter structure
            setSorter(newSorter.field ? { field: newSorter.field, order: newSorter.order, columnKey: newSorter.columnKey } : {});


            let pageToFetch = newTablePagination.current;
            // Reset to page 1 if sorting or filtering changes, but not if just paginating on current filters/sort
            if (extra.action === 'sort' || extra.action === 'filter') {
                pageToFetch = 1;
            }

            setPagination(prev => ({
                ...prev,
                current: pageToFetch,
                pageSize: newTablePagination.pageSize,
            }));
        }
    };

    const handleCreate = () => navigate('/user-admin/faculty-profile/create');
    const handleEdit = (id) => navigate(`/user-admin/faculty-profile/edit/${id}`);
    const handleView = (id) => navigate(`/user-admin/faculty-profile/view/${id}`);

    const handleArchiveToggle = async (record) => {
        const action = record.isArchived ? 'unarchive' : 'archive';
        const confirmAction = window.confirm(`Are you sure you want to ${action} profile for ${record.facultyFullNameForDisplay}?`);
        if (!confirmAction) return;

        setLoading(true);
        try {
            // Ensure your backend expects PATCH to /api/admin/faculty-profiles/:id/:action
            // where action is 'archive' or 'unarchive'
            const response = await fetch(`${API_URL}/api/admin/faculty-profiles/${record._id}/${action}`, {
                 method: 'PATCH',
                 headers: { 'Content-Type': 'application/json' }, // Good practice, though body might not be needed by backend
                 // body: JSON.stringify({ isArchived: !record.isArchived }) // Backend might infer from action or expect this
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Failed to ${action} profile.` }));
                throw new Error(errorData.message || `Could not ${action} profile.`);
            }
            message.success(`Profile ${action}d successfully!`);
            // Re-fetch current page data
            fetchData({
                page: pagination.current,
                pageSize: pagination.pageSize,
                showArchived: showArchived,
                search: searchTerm,
                filters: tableFilters,
                sorter: sorter
            });
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGlobalRefresh = () => {
        setSearchTerm('');
        setTableFilters({});
        setSorter({});
        setPagination(prev => ({ ...prev, current: 1, pageSize: 10 })); // This will trigger fetchData via useEffect
    };

    const handleSearchChange = (e) => setSearchTerm(e.target.value);

    // Trigger fetchData when search term is submitted (e.g., by pressing Enter or clicking search icon)
    const onGlobalSearch = () => {
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1 for new search
        // fetchData will be called by useEffect due to pagination/searchTerm change
    };

    const handleShowArchivedToggle = () => {
        setShowArchived(prev => !prev);
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1 when toggling archive view
    };

    const columns = [
        {
            title: 'Faculty Name',
            dataIndex: 'facultyFullNameForDisplay', // Use the processed display name
            key: 'facultyName', // Keep key consistent if sorter.columnKey uses it
            fixed: 'left',
            width: 200,
            sorter: true, // Indicates this column can be sorted
            sortOrder: sorter.columnKey === 'facultyName' ? sorter.order : null,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{text}</div>
                    {record.facultyEmailForDisplay && record.facultyEmailForDisplay !== 'N/A' && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{record.facultyEmailForDisplay}</div>
                    )}
                </div>
            ),
        },
        {
            title: 'Department',
            dataIndex: 'department',
            key: 'department',
            width: 150,
            sorter: true,
            sortOrder: sorter.columnKey === 'department' ? sorter.order : null,
            filters: [
                { text: 'ABM', value: 'ABM' }, { text: 'STEM', value: 'STEM' },
                { text: 'HUMSS', value: 'HUMSS' }, { text: 'GAS', value: 'GAS' },
                { text: 'TVL', value: 'TVL' }, { text: 'General', value: 'General' }
            ],
            filteredValue: tableFilters.department || null,
        },
        {
            title: 'Designation/Position',
            dataIndex: 'designation',
            key: 'designation',
            width: 180,
            sorter: true,
            sortOrder: sorter.columnKey === 'designation' ? sorter.order : null,
            filters: [
                { text: 'Subject Teacher', value: 'Subject Teacher' }, { text: 'Class Adviser', value: 'Class Adviser' },
                { text: 'Strand Coordinator', value: 'Strand Coordinator' }, { text: 'Level Coordinator', value: 'Level Coordinator' },
                { text: 'Academic Head', value: 'Academic Head' }, { text: 'Others', value: 'Others' }
            ],
            filteredValue: tableFilters.designation || null,
        },
        {
            title: 'Employment Type',
            dataIndex: 'employmentType',
            key: 'employmentType',
            width: 150,
            sorter: true,
            sortOrder: sorter.columnKey === 'employmentType' ? sorter.order : null,
            filters: [{ text: 'Full-Time', value: 'Full-Time' }, { text: 'Part-Time', value: 'Part-Time' }],
            filteredValue: tableFilters.employmentType || null,
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (text) => dayjs(text).isValid() ? dayjs.utc(text).local().format('YYYY-MM-DD HH:mm:ss') : 'N/A',
            sorter: true,
            sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            filteredValue: tableFilters.createdAt || null, // Expects an array from DateRangeFilter
        },
        {
            title: 'Updated At',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 180,
            render: (text) => dayjs(text).isValid() ? dayjs.utc(text).local().format('YYYY-MM-DD HH:mm:ss') : 'N/A',
            sorter: true,
            sortOrder: sorter.columnKey === 'updatedAt' ? sorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            filteredValue: tableFilters.updatedAt || null, // Expects an array from DateRangeFilter
        },
       {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 180,
            render: (_, record) => {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Button
                            icon={record.isArchived ? <FaEye /> : <FaPen />}
                            onClick={() => record.isArchived ? handleView(record._id) : handleEdit(record._id)}
                            style={{
                                width: '150px',
                                margin: '0 auto', // Centers button if container allows
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                            }}
                        >
                            {record.isArchived ? 'View Profile' : 'Edit Profile'}
                        </Button>
                        <Tooltip
                            title={record.isArchived ? "Restore this profile to the active list" : "Move this profile to the archive"}
                        >
                            <Button
                                icon={record.isArchived ? <FaBoxOpen /> : <FaBoxArchive />}
                                style={{
                                    width: '150px',
                                    margin: '0 auto',
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                }}
                                onClick={() => handleArchiveToggle(record)}
                                danger={!record.isArchived} // This makes the "Archive" button red
                            >
                                {record.isArchived ? 'Unarchive' : 'Archive'}
                            </Button>
                        </Tooltip>
                    </div>
                );
            }
        }
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            {showArchived && (
                <Tag 
                    className="archived-tag" 
                    color="volcano" 
                    style={{ marginBottom: '10px', display: 'block', textAlign: 'center', padding: '5px 0', fontSize: '14px' }}
                >
                    Viewing <strong>Archived Faculty Profiles</strong>
                </Tag>
            )}
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <p className="heading">
                            {showArchived ? "Archived Faculty Profiles" : "Manage Faculty Profiles"}
                        </p>
                    </div>
                    <div className="table-functions">
                        <div className="left-tools">
                            <Button icon={<FiFilter />} onClick={handleGlobalRefresh}>Clear All</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={() => fetchData({ page: pagination.current, pageSize: pagination.pageSize, showArchived, search: searchTerm, filters: tableFilters, sorter })}>Refresh</Button>
                             {/* Add Export button if needed */}
                            {/* <Button icon={<BiExport />} onClick={handleExport}>Export</Button> */}
                        </div>
                        <div className="right-tools">
                            <Input 
                                placeholder="Search name, email, department..." 
                                allowClear 
                                style={{ width: 250 }} 
                                value={searchTerm} 
                                onChange={handleSearchChange} 
                                onPressEnter={onGlobalSearch} // Trigger search on Enter
                                suffix={<FaSearch style={{ color: '#aaa', cursor: 'pointer' }} onClick={onGlobalSearch} />} 
                            />
                            <Button 
                                icon={showArchived ? <FaBoxOpen /> : <FaBoxArchive />} 
                                onClick={handleShowArchivedToggle}
                            >
                                {showArchived ? 'Show Active' : 'Show Archived'}
                            </Button>
                            {!showArchived && (
                                <Button 
                                    type="primary" 
                                    className="create-btn" 
                                    icon={<FaPlus />} 
                                    onClick={handleCreate}
                                >
                                    Create Profile
                                </Button>
                            )}
                        </div>
                    </div>
                    <Spin spinning={loading}>
                        <Table
                            className="faculty-profile-table" // Add a class for specific styling if needed
                            columns={columns}
                            dataSource={dataSource}
                            // loading={loading} // Spin component wraps Table now
                            scroll={{ x: 1500 }} // Adjust based on your column widths
                            pagination={{
                                ...pagination,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            bordered
                            onChange={handleTableChange}
                            rowKey="key"
                        />
                    </Spin>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default FacultyProfileTable;